import * as Dialog from "@radix-ui/react-dialog";
import { X, Save } from "lucide-react";
import {
  Theme,
  Box,
  Flex,
  ScrollArea,
  IconButton,
  Text,
  Tooltip,
  Button,
} from "@radix-ui/themes";
import { motion, AnimatePresence } from "motion/react";
import { cn } from "@/utils/cn";
import { useState, useCallback, useEffect } from "react";
import { ResizeHandle } from "./ResizeHandle";

interface SideDrawerProps {
  isOpen: boolean;
  title?: string;
  children: React.ReactNode;
  width?: string;
  headerActions?: React.ReactNode;
  /** Whether the drawer should be resizable */
  resizable?: boolean;
  /** Optional save button configuration */
  saveButton?: {
    /** Form ID that this button will submit */
    formId?: string;
    /** Text to show in tooltip and aria-label */
    label: string;
    /** Loading state for the save button */
    loading?: boolean;
    /** Called when the save button is clicked (in addition to form submission) */
    onClick?: () => void;
    /** Whether the button is disabled */
    disabled?: boolean;
  };
  onClose: () => void;
}

const SideDrawer = ({
  isOpen,
  title,
  children,
  width = "max-w-md",
  headerActions,
  saveButton,
  resizable = true,
  onClose,
}: SideDrawerProps) => {
  const [drawerWidth, setDrawerWidth] = useState(680);
  const [isResizing, setIsResizing] = useState(false);

  const handleResize = useCallback(
    (e: MouseEvent) => {
      if (!isResizing) return;
      const newWidth = Math.max(
        320,
        Math.min(window.innerWidth * 0.9, window.innerWidth - e.clientX)
      );
      setDrawerWidth(newWidth);
    },
    [isResizing]
  );

  useEffect(() => {
    if (!isResizing) return;

    const cleanup = () => {
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", handleResize);
    document.addEventListener("mouseup", () => setIsResizing(false));

    return () => {
      cleanup();
      document.removeEventListener("mousemove", handleResize);
      document.removeEventListener("mouseup", () => setIsResizing(false));
    };
  }, [isResizing, handleResize]);
  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <AnimatePresence>
          {isOpen && (
            <Theme>
              <Dialog.Overlay asChild>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  style={{
                    position: "fixed",
                    inset: 0,
                    backgroundColor: "rgba(0, 0, 0, 0.4)",
                    backdropFilter: "blur(2px)",
                    zIndex: 10000,
                  }}
                />
              </Dialog.Overlay>
              <Box
                position="fixed"
                inset="0"
                style={{ overflow: "hidden", zIndex: 10000 }}
              >
                <Box
                  position="absolute"
                  inset="0"
                  style={{ overflow: "hidden" }}
                >
                  <Flex
                    position="fixed"
                    direction="row"
                    justify="end"
                    style={{
                      top: 0,
                      right: 0,
                      bottom: 0,
                      paddingLeft: "var(--space-8)",
                      pointerEvents: "none",
                    }}
                  >
                    <Dialog.Content
                      aria-describedby={undefined}
                      data-testid="side-drawer"
                      className={cn(
                        "pointer-events-auto",
                        resizable ? "" : `w-screen ${width}`
                      )}
                      asChild
                      onInteractOutside={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onEscapeKeyDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      <motion.div
                        initial={{ x: "100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "100%" }}
                        transition={{
                          type: "spring",
                          damping: 25,
                          stiffness: 200,
                        }}
                        style={{
                          width: resizable ? `${drawerWidth}px` : "480px",
                        }}
                      >
                        <Flex
                          direction="column"
                          style={{
                            height: "100vh",
                            backgroundColor: "var(--color-panel-solid)",
                            boxShadow: "var(--shadow-5)",
                            position: "relative",
                          }}
                        >
                          {/* Resize Handle */}
                          {resizable && (
                            <ResizeHandle
                              onMouseDown={() => setIsResizing(true)}
                            />
                          )}
                          <Box
                            px="4"
                            py="4"
                            style={{
                              borderBottom: "1px solid var(--gray-a5)",
                            }}
                          >
                            <Flex align="center" justify="between">
                              <Dialog.Title asChild>
                                <Text size="5" weight="medium">
                                  {title}
                                </Text>
                              </Dialog.Title>
                              <Flex gap="2" align="center">
                                {headerActions && (
                                  <Flex gap="2" align="center">
                                    {headerActions}
                                  </Flex>
                                )}
                                {saveButton && (
                                  <Tooltip content={saveButton.label}>
                                    <Button
                                      type="button"
                                      loading={saveButton.loading}
                                      onClick={() => {
                                        saveButton.onClick?.();
                                        if (saveButton.formId) {
                                          const form = document.getElementById(
                                            saveButton.formId,
                                          ) as HTMLFormElement | null;
                                          if (form?.requestSubmit) {
                                            form.requestSubmit();
                                          } else if (form) {
                                            form.dispatchEvent(
                                              new Event("submit", { cancelable: true, bubbles: true }),
                                            );
                                          }
                                        }
                                      }}
                                      aria-label={saveButton.label}
                                    >
                                      <Save
                                        style={{
                                          width: "16px",
                                          height: "16px",
                                        }}
                                      />
                                      Save
                                    </Button>
                                  </Tooltip>
                                )}
                                <Dialog.Close asChild>
                                  <IconButton
                                    variant="ghost"
                                    color="gray"
                                    aria-label="Close panel"
                                  >
                                    <X />
                                  </IconButton>
                                </Dialog.Close>
                              </Flex>
                            </Flex>
                          </Box>
                          <ScrollArea
                            style={{
                              flex: 1,
                              position: "relative",
                            }}
                          >
                            <Box p="4">{children}</Box>
                          </ScrollArea>
                        </Flex>
                      </motion.div>
                    </Dialog.Content>
                  </Flex>
                </Box>
              </Box>
            </Theme>
          )}
        </AnimatePresence>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default SideDrawer;
